export const translations = {
  en: {
    instagram: {
      title: "SOP: Instagram Account Setup",
      intro:
        "Follow this step-by-step guide to set up your Instagram account for Spicy Cubes content. A properly set up account will help you maximize reach and engagement.",
      step1Title: "Sign Up",
      step1Text:
        "Create a BRAND NEW Instagram account for your Spicy Cubes content.",
      step2Title: "Username",
      step2Text: "Username examples:",
      step2Help:
        "If you need help with your username, please reach out on Geneva.",
      step2Tip: 'TIP: Include "Spicy" in your display name',
      step3Title: "Profile Photo",
      step3Text:
        "Use any photo of yourself or something else that you would normally use.",
      step4Title: "Set Up Professional Account",
      step4Steps: [
        "Tap your profile picture in the bottom right to go to your profile",
        "Tap Edit profile",
        "Tap Switch to a professional account",
        "Pick the digital creator category",
      ],
      step5Title: "Bio",
      step5Text: "Copy this for your bio, adding your discount code:",
      step5Bio:
        "For everyone asking about the cubes 👇🏼\n(*insert your discount code here*)\n#spicypartner",
      step5Link: "Add your affiliate link in the links section",
      step6Title: "Engagement (Warming Up Your Account)",
      step6Text:
        "Make sure you engage with other accounts so Instagram doesn't flag you as a bot:",
      step6Steps: [
        "Follow other creators",
        "For new accounts, spend 24-48 hours liking, commenting, etc., especially in your niche so Instagram knows you're a real user",
      ],
      filterTitle: "FILTER COMMENTS",
      filterText:
        "Go to Settings and privacy → Hidden words → Manage custom words and phrases",
      filterWords: "Filter these words one by one:",
      helpText:
        "If you have any questions regarding account setup, reach out to Ashli on Geneva.",
      backButton: "📱 Back to Content Brief 📱",
      contactText: "Questions? Contact",
    },
    tiktok: {
      title: "SOP: TikTok Account Setup",
      intro:
        "Follow this step-by-step guide to set up your TikTok account for Spicy Cubes content. A properly set up account will help you maximize reach and engagement.",
      step1Title: "Sign Up",
      step1Text:
        "Create a BRAND NEW TikTok account for your Spicy Cubes content.",
      step2Title: "Username",
      step2Text: "Username examples:",
      step2Help:
        "If you need help with your username, please reach out on Geneva.",
      step2Tip: 'TIP: Include "Spicy" in your display name',
      step3Title: "Profile Photo",
      step3Text:
        "Use any picture you would normally use for your social media profiles.",
      step4Title: "Bio",
      step4Text: "Copy this for your bio, adding your discount code:",
      step4Bio:
        "For everyone asking about the cubes 👇🏼\n(*insert your discount code here*)\n#spicypartner\nSpicycubes.co 🔗",
      step4Note:
        "*Once your account reaches 1K followers, you will add your affiliate link to the links section*",
      step5Title: "Engagement (Warming Up Your Account)",
      step5Text:
        "Make sure you engage with other accounts so TikTok doesn't flag you as a bot:",
      step5Steps: [
        "Follow other creators (not Spicy Cubes creators, just other content creators)",
        "Repost their content, like videos, leave comments, etc. for ~3 days so TikTok knows you're a real user",
        "Avoid posting or performing any actions that might seem spammy during this time period",
      ],
      warmTitle: 'How to Know If the Account Is "Warmed":',
      warmText:
        "After 3 days of scrolling, commenting, and liking (without posting), the account should be warmed.",
      warmTest: "Test for warming:",
      warmSteps: [
        "Scroll through your For You Page (FYP)",
        "Like a video and watch 5–6 more videos",
        "Scroll back to check if the liked video remains liked",
        "If the likes stick, your account is warmed and ready for regular posting",
      ],
      filterTitle: "FILTER COMMENTS",
      filterText:
        "Go to Settings and Privacy → Privacy → Comments → Filter Specific Keywords",
      filterWords: "Filter these specific keywords:",
      helpText:
        "If you have any questions regarding account setup, reach out to Ashli on Geneva.",
      backButton: "📱 Back to Content Brief 📱",
      contactText: "Questions? Contact",
    },
    contentBrief: {
      passwordTitle: "Protected Content",
      passwordPrompt: "Enter password",
      accessButton: "Access Content Brief",
      welcomeTitle: "Welcome to the Spicy Cubes Creator Program! 🔥",
      welcomeText:
        "Follow this checklist to get started. Click any item to jump to that section.",
      yourJourney: "Your Journey",
      progress: "Progress",
      checklistItems: [
        "Create & warm up accounts",
        "Watch introduction video",
        "Learn 3-step hook formula",
        "Complete training videos",
        "Submit first draft to Ashli",
        "Start posting daily",
        "Engage with comments using CTAs",
      ],
      step1Title: "Step 1: Create & Warm Up Your Accounts",
      step1Important:
        "⚠️ IMPORTANT: Complete account setup BEFORE posting any content",
      createAccounts: "Create Your Accounts",
      createAccountsText: "Set up accounts on the following platforms:",
      tiktokPlatform: "TikTok",
      tiktokDesc: "Primary platform for viral content",
      instagramPlatform: "Instagram Reels",
      instagramDesc: "Cross-post for maximum reach",
      warmUpTitle: "Warm Up Your Accounts",
      warmUpText:
        "Before posting Spicy Cubes content, establish your account as authentic:",
      warmUpSteps: [
        "Study and engage for 3 days",
        "Make drafts for first 5 videos",
        "Post consistently",
        "Get approval",
      ],
      warmUpNote:
        "Note: Account warming typically takes around 3 days. This prevents shadowbanning and ensures maximum reach.",
      step2Title: "Step 2: Watch Introduction Video",
      step2Text:
        "Start here! Ashli will introduce you to the brand and explain your content strategy.",
      step3Title: "Step 3: Master the 3-Step Hook Formula",
      step3Important: "⚠️ CRITICAL: Every video MUST include all 3 hook types",
      visualHook: "Visual Hook",
      visualHookDesc:
        "First 3 seconds: Bold action that makes viewers stop scrolling",
      textHook: "Text Hook",
      textHookDesc: "On-screen text that creates anticipation",
      verbalHook: "Verbal Hook",
      verbalHookDesc: "First 5 seconds: Phrase that sparks curiosity",
      watchHooksTraining: "Watch: Hooks Training",
      step4Title: "Step 4: Complete Training Videos",
      ctaBestPractices: "CTA Best Practices",
      ctaDesc: "Learn how to convert viewers into customers",
      ctaWarning:
        "⚠️ NEVER say: aphrodisiac, gummies, viagra, or make medical claims",
      ctaInstead: "Instead use:",
      step5Title: "Step 5: Submit Your First Draft",
      step5Important: "Before posting your first video, you must:",
      step5Steps: [
        "Film your first draft following the script templates",
        "Send it to Ashli on Slack for approval",
        "Wait for feedback before posting",
      ],
      getScriptTemplates: "Get Script Templates",
      contactAshliSlack: "Contact Ashli on Slack",
      step6Title: "Step 6: Start Posting Daily",
      minRequirements: "📅 Minimum Requirements",
      minRequirementsList: [
        "1 post per day minimum",
        "30 videos monthly",
        "Wait 1+ hour between posts",
      ],
      maxEarnings: "💰 Maximize Earnings",
      maxEarningsList: [
        "Post multiple times per day",
        "More posts = more commissions",
        "Compete for ranking bonuses",
      ],
      contentGuidelines: "📋 Content Guidelines",
      tone: "Tone",
      toneDesc:
        "When telling a spicy story, speak playfully as if you're talking to your best friend on FaceTime. Keep content relatable and real. It should NOT sound like a sales pitch.",
      restrictedWords: "Restricted Words",
      restrictedWordsWarning: "⚠️ DO NOT USE:",
      restrictedWordsList:
        "Sex, orgasm, horny, viagra, aphrodisiac, gummies (in medical context), or any profanity that will get flagged",
      alternativeWords: "Alternative Words",
      alternativeWordsList:
        "Use: Spicy, seggs, smeggs, date night gummies, adult candy (or any other creative alternatives that won't get flagged)",
      communityGuidelines: "Community Guidelines",
      communityGuidelinesList: [
        "If you get a violation: Appeal immediately and notify us on Slack so we can understand why and prevent future violations",
        "NEVER include: Inappropriate content involving children or references to date rape",
        "For prank content: Always note you got consent",
        "No medical claims: Never mention medical benefits or compare to prescription drugs",
      ],
      step7Title: "Step 7: Engage & Convert",
      platformTips: "Platform-Specific Tips",
      tiktokTip:
        "Video replies work extremely well. Always respond to questions.",
      instagramTip:
        "Respond to comments and pin them instead of video replies.",
      engagementReminder:
        "Remember: Engagement drives virality. The more you interact, the more the algorithm promotes your content!",
      needHelp: "Need Help?",
      needHelpText: "Reach out to Ashli for support and guidance",
      emailAshli: "Email Ashli",
      messageSlack: "Message on Slack",
      viewScripts: "View Scripts →",
    },
  },
  es: {
    instagram: {
      title: "POP: Configuración de Cuenta de Instagram",
      intro:
        "Sigue esta guía paso a paso para configurar tu cuenta de Instagram para el contenido de Spicy Cubes. Una cuenta bien configurada te ayudará a maximizar el alcance y el compromiso.",
      step1Title: "Regístrate",
      step1Text:
        "Crea una cuenta de Instagram COMPLETAMENTE NUEVA para tu contenido de Spicy Cubes.",
      step2Title: "Nombre de usuario",
      step2Text: "Ejemplos de nombres de usuario:",
      step2Help:
        "Si necesitas ayuda con tu nombre de usuario, comunícate en Geneva.",
      step2Tip: 'CONSEJO: Incluye "Spicy" en tu nombre de visualización',
      step3Title: "Foto de perfil",
      step3Text: "Usa cualquier foto tuya o algo que normalmente usarías.",
      step4Title: "Configurar cuenta profesional",
      step4Steps: [
        "Toca tu foto de perfil en la esquina inferior derecha para ir a tu perfil",
        "Toca Editar perfil",
        "Toca Cambiar a cuenta profesional",
        "Elige la categoría creador digital",
      ],
      step5Title: "Biografía",
      step5Text:
        "Copia esto para tu biografía, agregando tu código de descuento:",
      step5Bio:
        "Para todos los que preguntan sobre los cubos 👇🏼\n(*inserta tu código de descuento aquí*)\n#spicypartner",
      step5Link: "Agrega tu enlace de afiliado en la sección de enlaces",
      step6Title: "Compromiso (Calentando tu cuenta)",
      step6Text:
        "Asegúrate de interactuar con otras cuentas para que Instagram no te marque como bot:",
      step6Steps: [
        "Sigue a otros creadores",
        "Para cuentas nuevas, pasa 24-48 horas dando me gusta, comentando, etc., especialmente en tu nicho para que Instagram sepa que eres un usuario real",
      ],
      filterTitle: "FILTRAR COMENTARIOS",
      filterText:
        "Ve a Configuración y privacidad → Palabras ocultas → Administrar palabras y frases personalizadas",
      filterWords: "Filtra estas palabras una por una:",
      helpText:
        "Si tienes preguntas sobre la configuración de la cuenta, comunícate con Ashli en Geneva.",
      backButton: "📱 Volver al Resumen de Contenido 📱",
      contactText: "¿Preguntas? Contacta",
    },
    tiktok: {
      title: "POP: Configuración de Cuenta de TikTok",
      intro:
        "Sigue esta guía paso a paso para configurar tu cuenta de TikTok para el contenido de Spicy Cubes. Una cuenta bien configurada te ayudará a maximizar el alcance y el compromiso.",
      step1Title: "Regístrate",
      step1Text:
        "Crea una cuenta de TikTok COMPLETAMENTE NUEVA para tu contenido de Spicy Cubes.",
      step2Title: "Nombre de usuario",
      step2Text: "Ejemplos de nombres de usuario:",
      step2Help:
        "Si necesitas ayuda con tu nombre de usuario, comunícate en Geneva.",
      step2Tip: 'CONSEJO: Incluye "Spicy" en tu nombre de visualización',
      step3Title: "Foto de perfil",
      step3Text:
        "Usa cualquier imagen que normalmente usarías para tus perfiles de redes sociales.",
      step4Title: "Biografía",
      step4Text:
        "Copia esto para tu biografía, agregando tu código de descuento:",
      step4Bio:
        "Para todos los que preguntan sobre los cubos 👇🏼\n(*inserta tu código de descuento aquí*)\n#spicypartner\nSpicycubes.co 🔗",
      step4Note:
        "*Una vez que tu cuenta alcance 1K seguidores, agregarás tu enlace de afiliado a la sección de enlaces*",
      step5Title: "Compromiso (Calentando tu cuenta)",
      step5Text:
        "Asegúrate de interactuar con otras cuentas para que TikTok no te marque como bot:",
      step5Steps: [
        "Sigue a otros creadores (no creadores de Spicy Cubes, solo otros creadores de contenido)",
        "Republica su contenido, da me gusta a videos, deja comentarios, etc. durante ~3 días para que TikTok sepa que eres un usuario real",
        "Evita publicar o realizar acciones que puedan parecer spam durante este período",
      ],
      warmTitle: 'Cómo saber si la cuenta está "calentada":',
      warmText:
        "Después de 3 días de desplazarte, comentar y dar me gusta (sin publicar), la cuenta debería estar calentada.",
      warmTest: "Prueba para calentar:",
      warmSteps: [
        "Desplázate por tu página Para Ti (FYP)",
        "Da me gusta a un video y mira 5-6 videos más",
        "Desplázate hacia atrás para verificar si el video que te gustó sigue marcado",
        "Si los me gusta permanecen, tu cuenta está calentada y lista para publicar regularmente",
      ],
      filterTitle: "FILTRAR COMENTARIOS",
      filterText:
        "Ve a Configuración y privacidad → Privacidad → Comentarios → Filtrar palabras clave específicas",
      filterWords: "Filtra estas palabras clave específicas:",
      helpText:
        "Si tienes preguntas sobre la configuración de la cuenta, comunícate con Ashli en Geneva.",
      backButton: "📱 Volver al Resumen de Contenido 📱",
      contactText: "¿Preguntas? Contacta",
    },
    contentBrief: {
      passwordTitle: "Contenido Protegido",
      passwordPrompt: "Ingresa la contraseña",
      accessButton: "Acceder al Resumen de Contenido",
      welcomeTitle: "¡Bienvenido al Programa de Creadores Spicy Cubes! 🔥",
      welcomeText:
        "Sigue esta lista de verificación para comenzar. Haz clic en cualquier elemento para ir a esa sección.",
      yourJourney: "Tu Viaje",
      progress: "Progreso",
      checklistItems: [
        "Crear y calentar cuentas",
        "Ver video de introducción",
        "Aprender la fórmula de ganchos de 3 pasos",
        "Completar videos de capacitación",
        "Enviar primer borrador a Ashli",
        "Comenzar a publicar diariamente",
        "Interactuar con comentarios usando CTAs",
      ],
      step1Title: "Paso 1: Crea y Calienta tus Cuentas",
      step1Important:
        "⚠️ IMPORTANTE: Completa la configuración de la cuenta ANTES de publicar cualquier contenido",
      createAccounts: "Crea tus Cuentas",
      createAccountsText: "Configura cuentas en las siguientes plataformas:",
      tiktokPlatform: "TikTok",
      tiktokDesc: "Plataforma principal para contenido viral",
      instagramPlatform: "Instagram Reels",
      instagramDesc: "Publica cruzado para máximo alcance",
      warmUpTitle: "Calienta tus Cuentas",
      warmUpText:
        "Antes de publicar contenido de Spicy Cubes, establece tu cuenta como auténtica:",
      warmUpSteps: [
        "Estudia e interactúa durante 3 días",
        "Haz borradores para los primeros 5 videos",
        "Publica consistentemente",
        "Obtén aprobación",
      ],
      warmUpNote:
        "Nota: El calentamiento de la cuenta generalmente toma alrededor de 3 días. Esto previene el shadowban y asegura el máximo alcance.",
      step2Title: "Paso 2: Ver Video de Introducción",
      step2Text:
        "¡Comienza aquí! Ashli te presentará la marca y explicará tu estrategia de contenido.",
      step3Title: "Paso 3: Domina la Fórmula de Ganchos de 3 Pasos",
      step3Important:
        "⚠️ CRÍTICO: Cada video DEBE incluir los 3 tipos de ganchos",
      visualHook: "Gancho Visual",
      visualHookDesc:
        "Primeros 3 segundos: Acción audaz que hace que los espectadores se detengan",
      textHook: "Gancho de Texto",
      textHookDesc: "Texto en pantalla que genera anticipación",
      verbalHook: "Gancho Verbal",
      verbalHookDesc: "Primeros 5 segundos: Frase que despierta curiosidad",
      watchHooksTraining: "Ver: Entrenamiento de Ganchos",
      step4Title: "Paso 4: Completar Videos de Capacitación",
      ctaBestPractices: "Mejores Prácticas de CTA",
      ctaDesc: "Aprende cómo convertir espectadores en clientes",
      ctaWarning:
        "⚠️ NUNCA digas: afrodisíaco, gomitas, viagra, o hagas afirmaciones médicas",
      ctaInstead: "En su lugar usa:",
      step5Title: "Paso 5: Envía tu Primer Borrador",
      step5Important: "IMPORTANTE: Antes de publicar tu primer video, debes:",
      step5Steps: [
        "Filmar tu primer borrador siguiendo las plantillas de guión",
        "Enviarlo a Ashli en Slack para aprobación",
        "Esperar comentarios antes de publicar",
      ],
      getScriptTemplates: "Obtener Plantillas de Guión",
      contactAshliSlack: "Contactar a Ashli en Slack",
      step6Title: "Paso 6: Comenzar a Publicar Diariamente",
      minRequirements: "📅 Requisitos Mínimos",
      minRequirementsList: [
        "1 publicación por día mínimo",
        "30 videos mensuales",
        "Esperar 1+ hora entre publicaciones",
      ],
      maxEarnings: "💰 Maximiza Ganancias",
      maxEarningsList: [
        "Publica varias veces al día",
        "Más publicaciones = más comisiones",
        "Compite por bonos de clasificación",
      ],
      contentGuidelines: "📋 Pautas de Contenido",
      tone: "Tono",
      toneDesc:
        "Al contar una historia picante, habla de manera juguetona como si estuvieras hablando con tu mejor amiga en FaceTime. Mantén el contenido relatable y real. NO debe sonar como un discurso de ventas.",
      restrictedWords: "Palabras Restringidas",
      restrictedWordsWarning: "⚠️ NO USES:",
      restrictedWordsList:
        "Sexo, orgasmo, excitado, viagra, afrodisíaco, gomitas (en contexto médico), o cualquier palabra grosera que será marcada",
      alternativeWords: "Palabras Alternativas",
      alternativeWordsList:
        "Usa: Picante, seggs, smeggs, gomitas para cita nocturna, dulces para adultos (o cualquier otra alternativa creativa que no será marcada)",
      communityGuidelines: "Pautas de la Comunidad",
      communityGuidelinesList: [
        "Si recibes una violación: Apela inmediatamente y notifícanos en Slack para que podamos entender por qué y prevenir futuras violaciones",
        "NUNCA incluyas: Contenido inapropiado que involucre a niños o referencias a violación en citas",
        "Para contenido de bromas: Siempre nota que recibiste consentimiento",
        "Sin afirmaciones médicas: Nunca menciones beneficios médicos o compares con medicamentos recetados",
      ],
      step7Title: "Paso 7: Interactuar y Convertir",
      platformTips: "Consejos Específicos de Plataforma",
      tiktokTip:
        "Las respuestas en video funcionan extremadamente bien. Siempre responde a las preguntas.",
      instagramTip:
        "Responde a los comentarios y fíjalos en lugar de respuestas en video.",
      engagementReminder:
        "Recuerda: El compromiso impulsa la viralidad. ¡Cuanto más interactúas, más el algoritmo promueve tu contenido!",
      needHelp: "¿Necesitas Ayuda?",
      needHelpText: "Contacta a Ashli para apoyo y orientación",
      emailAshli: "Enviar correo a Ashli",
      messageSlack: "Mensaje en Slack",
      viewScripts: "Ver Guiones →",
    },
  },
  es: {
    contentBrief: {
      passwordTitle: "Contenido Protegido",
      passwordPrompt: "Ingresa la contraseña",
      accessButton: "Acceder al Resumen de Contenido",
      welcomeTitle: "¡Bienvenido al Programa de Creadores Spicy Cubes! 🔥",
      welcomeText:
        "Sigue esta lista de verificación para comenzar. Haz clic en cualquier elemento para ir a esa sección.",
      yourJourney: "Tu Viaje",
      progress: "Progreso",
      checklistItems: [
        "Crear y calentar cuentas",
        "Ver video de introducción",
        "Aprender la fórmula de ganchos de 3 pasos",
        "Completar videos de capacitación",
        "Enviar primer borrador a Ashli",
        "Comenzar a publicar diariamente",
        "Interactuar con comentarios usando CTAs",
      ],
      step1Title: "Paso 1: Crea y Calienta tus Cuentas",
      step1Important:
        "⚠️ IMPORTANTE: Completa la configuración de la cuenta ANTES de publicar cualquier contenido",
      createAccounts: "Crea tus Cuentas",
      createAccountsText: "Configura cuentas en las siguientes plataformas:",
      tiktokPlatform: "TikTok",
      tiktokDesc: "Plataforma principal para contenido viral",
      instagramPlatform: "Instagram Reels",
      instagramDesc: "Publica cruzado para máximo alcance",
      warmUpTitle: "Calienta tus Cuentas",
      warmUpText:
        "Antes de publicar contenido de Spicy Cubes, establece tu cuenta como auténtica:",
      warmUpSteps: [
        "Estudia e interactúa durante 3 días",
        "Haz borradores para los primeros 5 videos",
        "Publica consistentemente",
        "Obtén aprobación",
      ],
      warmUpNote:
        "Nota: El calentamiento de la cuenta generalmente toma alrededor de 3 días. Esto previene el shadowban y asegura el máximo alcance.",
      step2Title: "Paso 2: Ver Video de Introducción",
      step2Text:
        "¡Comienza aquí! Ashli te presentará la marca y explicará tu estrategia de contenido.",
      step3Title: "Paso 3: Domina la Fórmula de Ganchos de 3 Pasos",
      step3Important:
        "⚠️ CRÍTICO: Cada video DEBE incluir los 3 tipos de ganchos",
      visualHook: "Gancho Visual",
      visualHookDesc:
        "Primeros 3 segundos: Acción audaz que hace que los espectadores se detengan",
      textHook: "Gancho de Texto",
      textHookDesc: "Texto en pantalla que genera anticipación",
      verbalHook: "Gancho Verbal",
      verbalHookDesc: "Primeros 5 segundos: Frase que despierta curiosidad",
      watchHooksTraining: "Ver: Entrenamiento de Ganchos",
      step4Title: "Paso 4: Completar Videos de Capacitación",
      ctaBestPractices: "Mejores Prácticas de CTA",
      ctaDesc: "Aprende cómo convertir espectadores en clientes",
      ctaWarning:
        "⚠️ NUNCA digas: afrodisíaco, gomitas, viagra, o hagas afirmaciones médicas",
      ctaInstead: "En su lugar usa:",
      step5Title: "Paso 5: Envía tu Primer Borrador",
      step5Important: "IMPORTANTE: Antes de publicar tu primer video, debes:",
      step5Steps: [
        "Filmar tu primer borrador siguiendo las plantillas de guión",
        "Enviarlo a Ashli en Slack para aprobación",
        "Esperar comentarios antes de publicar",
      ],
      getScriptTemplates: "Obtener Plantillas de Guión",
      contactAshliSlack: "Contactar a Ashli en Slack",
      step6Title: "Paso 6: Comenzar a Publicar Diariamente",
      minRequirements: "📅 Requisitos Mínimos",
      minRequirementsList: [
        "1 publicación por día mínimo",
        "30 videos mensuales",
        "Esperar 1+ hora entre publicaciones",
      ],
      maxEarnings: "💰 Maximiza Ganancias",
      maxEarningsList: [
        "Publica varias veces al día",
        "Más publicaciones = más comisiones",
        "Compite por bonos de clasificación",
      ],
      contentGuidelines: "📋 Pautas de Contenido",
      tone: "Tono",
      toneDesc:
        "Al contar una historia picante, habla de manera juguetona como si estuvieras hablando con tu mejor amiga en FaceTime. Mantén el contenido relatable y real. NO debe sonar como un discurso de ventas.",
      restrictedWords: "Palabras Restringidas",
      restrictedWordsWarning: "⚠️ NO USES:",
      restrictedWordsList:
        "Sexo, orgasmo, excitado, viagra, afrodisíaco, gomitas (en contexto médico), o cualquier palabra grosera que será marcada",
      alternativeWords: "Palabras Alternativas",
      alternativeWordsList:
        "Usa: Picante, seggs, smeggs, gomitas para cita nocturna, dulces para adultos (o cualquier otra alternativa creativa que no será marcada)",
      communityGuidelines: "Pautas de la Comunidad",
      communityGuidelinesList: [
        "Si recibes una violación: Apela inmediatamente y notifícanos en Slack para que podamos entender por qué y prevenir futuras violaciones",
        "NUNCA incluyas: Contenido inapropiado que involucre a niños o referencias a violación en citas",
        "Para contenido de bromas: Siempre nota que recibiste consentimiento",
        "Sin afirmaciones médicas: Nunca menciones beneficios médicos o compares con medicamentos recetados",
      ],
      step7Title: "Paso 7: Interactuar y Convertir",
      platformTips: "Consejos Específicos de Plataforma",
      tiktokTip:
        "Las respuestas en video funcionan extremadamente bien. Siempre responde a las preguntas.",
      instagramTip:
        "Responde a los comentarios y fíjalos en lugar de respuestas en video.",
      engagementReminder:
        "Recuerda: El compromiso impulsa la viralidad. ¡Cuanto más interactúas, más el algoritmo promueve tu contenido!",
      needHelp: "¿Necesitas Ayuda?",
      needHelpText: "Contacta a Ashli para apoyo y orientación",
      emailAshli: "Enviar correo a Ashli",
      messageSlack: "Mensaje en Slack",
      viewScripts: "Ver Guiones →",
    },
    instagram: {
      title: "POP: Configuración de Cuenta de Instagram",
      intro:
        "Sigue esta guía paso a paso para configurar tu cuenta de Instagram para el contenido de Spicy Cubes. Una cuenta bien configurada te ayudará a maximizar el alcance y el compromiso.",
      step1Title: "Regístrate",
      step1Text:
        "Crea una cuenta de Instagram COMPLETAMENTE NUEVA para tu contenido de Spicy Cubes.",
      step2Title: "Nombre de usuario",
      step2Text: "Ejemplos de nombres de usuario:",
      step2Help:
        "Si necesitas ayuda con tu nombre de usuario, comunícate en Geneva.",
      step2Tip: 'CONSEJO: Incluye "Spicy" en tu nombre de visualización',
      step3Title: "Foto de perfil",
      step3Text: "Usa cualquier foto tuya o algo que normalmente usarías.",
      step4Title: "Configurar cuenta profesional",
      step4Steps: [
        "Toca tu foto de perfil en la esquina inferior derecha para ir a tu perfil",
        "Toca Editar perfil",
        "Toca Cambiar a cuenta profesional",
        "Elige la categoría creador digital",
      ],
      step5Title: "Biografía",
      step5Text:
        "Copia esto para tu biografía, agregando tu código de descuento:",
      step5Bio:
        "Para todos los que preguntan sobre los cubos 👇🏼\n(*inserta tu código de descuento aquí*)\n#spicypartner",
      step5Link: "Agrega tu enlace de afiliado en la sección de enlaces",
      step6Title: "Compromiso (Calentando tu cuenta)",
      step6Text:
        "Asegúrate de interactuar con otras cuentas para que Instagram no te marque como bot:",
      step6Steps: [
        "Sigue a otros creadores",
        "Para cuentas nuevas, pasa 24-48 horas dando me gusta, comentando, etc., especialmente en tu nicho para que Instagram sepa que eres un usuario real",
      ],
      filterTitle: "FILTRAR COMENTARIOS",
      filterText:
        "Ve a Configuración y privacidad → Palabras ocultas → Administrar palabras y frases personalizadas",
      filterWords: "Filtra estas palabras una por una:",
      helpText:
        "Si tienes preguntas sobre la configuración de la cuenta, comunícate con Ashli en Geneva.",
      backButton: "📱 Volver al Resumen de Contenido 📱",
      contactText: "¿Preguntas? Contacta",
    },
    tiktok: {
      title: "POP: Configuración de Cuenta de TikTok",
      intro:
        "Sigue esta guía paso a paso para configurar tu cuenta de TikTok para el contenido de Spicy Cubes. Una cuenta bien configurada te ayudará a maximizar el alcance y el compromiso.",
      step1Title: "Regístrate",
      step1Text:
        "Crea una cuenta de TikTok COMPLETAMENTE NUEVA para tu contenido de Spicy Cubes.",
      step2Title: "Nombre de usuario",
      step2Text: "Ejemplos de nombres de usuario:",
      step2Help:
        "Si necesitas ayuda con tu nombre de usuario, comunícate en Geneva.",
      step2Tip: 'CONSEJO: Incluye "Spicy" en tu nombre de visualización',
      step3Title: "Foto de perfil",
      step3Text:
        "Usa cualquier imagen que normalmente usarías para tus perfiles de redes sociales.",
      step4Title: "Biografía",
      step4Text:
        "Copia esto para tu biografía, agregando tu código de descuento:",
      step4Bio:
        "Para todos los que preguntan sobre los cubos 👇🏼\n(*inserta tu código de descuento aquí*)\n#spicypartner\nSpicycubes.co 🔗",
      step4Note:
        "*Una vez que tu cuenta alcance 1K seguidores, agregarás tu enlace de afiliado a la sección de enlaces*",
      step5Title: "Compromiso (Calentando tu cuenta)",
      step5Text:
        "Asegúrate de interactuar con otras cuentas para que TikTok no te marque como bot:",
      step5Steps: [
        "Sigue a otros creadores (no creadores de Spicy Cubes, solo otros creadores de contenido)",
        "Republica su contenido, da me gusta a videos, deja comentarios, etc. durante ~3 días para que TikTok sepa que eres un usuario real",
        "Evita publicar o realizar acciones que puedan parecer spam durante este período",
      ],
      warmTitle: 'Cómo saber si la cuenta está "calentada":',
      warmText:
        "Después de 3 días de desplazarte, comentar y dar me gusta (sin publicar), la cuenta debería estar calentada.",
      warmTest: "Prueba para calentar:",
      warmSteps: [
        "Desplázate por tu página Para Ti (FYP)",
        "Da me gusta a un video y mira 5-6 videos más",
        "Desplázate hacia atrás para verificar si el video que te gustó sigue marcado",
        "Si los me gusta permanecen, tu cuenta está calentada y lista para publicar regularmente",
      ],
      filterTitle: "FILTRAR COMENTARIOS",
      filterText:
        "Ve a Configuración y privacidad → Privacidad → Comentarios → Filtrar palabras clave específicas",
      filterWords: "Filtra estas palabras clave específicas:",
      helpText:
        "Si tienes preguntas sobre la configuración de la cuenta, comunícate con Ashli en Geneva.",
      backButton: "📱 Volver al Resumen de Contenido 📱",
      contactText: "¿Preguntas? Contacta",
    },
  },
};
